/**
 * Copyright (c), Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import React from 'react';
import { 
  Box, 
  Typography, 
  LinearProgress,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';

export type GenerationStep = 
  | 'preparing' 
  | 'outline' 
  | 'scene-1' 
  | 'scene-2'
  | 'scene-3' 
  | 'scene-4' 
  | 'scene-5'
  | 'scene-6'
  | 'scene-7'
  | 'combining'
  | 'saving'
  | 'error'
  | 'complete';

interface GenerationProgressProps {
  currentStep: GenerationStep;
  totalScenes: number;
  error?: string | null;
  isGenerating: boolean;
}

const GenerationProgress: React.FC<GenerationProgressProps> = ({
  currentStep,
  totalScenes,
  error = null,
  isGenerating
}) => {
  // Map steps to readable descriptions and progress percentages
  const getStepDetails = () => {
    // Base steps: preparing, outline, scene(s), combining, saving
    const totalSteps = 3 + totalScenes; // preparing + outline + scenes + combining + saving
    
    if (currentStep === 'preparing') {
      return {
        description: 'Preparing script generation...',
        progress: (1 / totalSteps) * 100
      };
    } else if (currentStep === 'outline') {
      return {
        description: 'Creating scene outline...',
        progress: (2 / totalSteps) * 100
      };
    } else if (currentStep.startsWith('scene-')) {
      const sceneNum = parseInt(currentStep.split('-')[1], 10);
      return {
        description: `Generating scene ${sceneNum} of ${totalScenes}...`,
        progress: ((2 + sceneNum) / totalSteps) * 100
      };
    } else if (currentStep === 'combining') {
      return {
        description: 'Combining scenes into complete script...',
        progress: ((2 + totalScenes) / totalSteps) * 100
      };
    } else if (currentStep === 'saving') {
      return {
        description: 'Saving script to database...',
        progress: ((3 + totalScenes) / totalSteps) * 100
      };
    } else if (currentStep === 'complete') {
      return {
        description: 'Script generation complete!',
        progress: 100
      };
    } else {
      return {
        description: 'Processing...',
        progress: 50
      };
    }
  };

  const { description, progress } = getStepDetails();

  // Only show when generating is in progress
  if (!isGenerating && currentStep !== 'complete' && currentStep !== 'error') {
    return null;
  }

  // Show error message if error occurred
  if (currentStep === 'error') {
    return (
      <Snackbar 
        open={true} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {error || 'An error occurred during script generation'}
        </Alert>
      </Snackbar>
    );
  }

  // Show completion message briefly
  if (currentStep === 'complete') {
    return (
      <Snackbar 
        open={true} 
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Script generated successfully! Redirecting to script page...
        </Alert>
      </Snackbar>
    );
  }

  return (
    <Snackbar 
      open={true} 
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Paper sx={{ 
        p: 2, 
        width: '100%', 
        maxWidth: 500,
        bgcolor: 'background.paper',
        boxShadow: 3,
        borderRadius: 2
      }}>
        <Typography variant="subtitle1" gutterBottom>
          {description}
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ mt: 1, mb: 1, height: 8, borderRadius: 4 }}
        />
        <Typography variant="caption" color="text.secondary">
          This may take a few minutes. Please do not close this page.
        </Typography>
      </Paper>
    </Snackbar>
  );
};

export default GenerationProgress;
